/**
 * EditorPageCoverPicture styled components using @compiled/react
 */
import type { StyledProps } from '@compiled/react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE } from '../pageCoverPictureConstants.ts';

const COVER_IMAGE_FIXED_HEIGHT = 147;

import type { CoverPictureWrapperProps } from './EditorPageCoverPictureWrapper.tsx';
import type { ComponentType, HTMLAttributes, ReactNode, ClassAttributes } from 'react';

// if page has cover picture or is uploading picture, make space for the photo
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- To migrate as part of go/ui-styling-standard
export const CoverPictureWrapperStyled: ComponentType<
	HTMLAttributes<HTMLDivElement> & {
		hasCoverPicture: boolean;
		isUploadingPicture: boolean;
		isReadOnly?: boolean;
		isFixedWidthImageOption?: boolean;
		isSmartSpaceOverview?: boolean;
		smartSpaceOverviewHeight?: number;
		width?: number;
		dynamicCoverImageHeight?: number;
	} & {
		children?: ReactNode | undefined;
	} & ClassAttributes<HTMLDivElement> &
		StyledProps
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
> = styled.div<CoverPictureWrapperProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({
		hasCoverPicture,
		isUploadingPicture,
		isFixedWidthImageOption,
		smartSpaceOverviewHeight,
		dynamicCoverImageHeight,
	}) =>
		(hasCoverPicture || isUploadingPicture) && smartSpaceOverviewHeight
			? `${smartSpaceOverviewHeight}px`
			: (hasCoverPicture || isUploadingPicture) && isFixedWidthImageOption
				? `${COVER_IMAGE_FIXED_HEIGHT}px`
				: (hasCoverPicture || isUploadingPicture) && !isFixedWidthImageOption
					? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
						CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE
					: hasCoverPicture || isUploadingPicture
						? `${dynamicCoverImageHeight}px`
						: '0px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ width, isFixedWidthImageOption, hasCoverPicture, isUploadingPicture }) =>
		(hasCoverPicture || isUploadingPicture) && isFixedWidthImageOption
			? `800px`
			: (hasCoverPicture || isUploadingPicture) && !isFixedWidthImageOption
				? '100%'
				: (hasCoverPicture || isUploadingPicture) && width
					? `${width}px`
					: `100%`,
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: ({ isReadOnly }) => (!isReadOnly ? 'pointer' : 'auto'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginBottom: ({ isSmartSpaceOverview }) =>
		isSmartSpaceOverview ? token('space.0') : token('space.100'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	marginTop: ({ hasCoverPicture, isUploadingPicture, isSmartSpaceOverview }) =>
		isSmartSpaceOverview
			? token('space.0')
			: hasCoverPicture || isUploadingPicture
				? token('space.075')
				: token('space.0'),
	borderRadius: 'unset',
});
